/**
 * `useFilteredQuery` — the dashboard's one rule for "the answer on screen does
 * not belong to the question on screen".
 *
 * The filtered hooks that set `placeholderData: keepPreviousData` — most of
 * them, but NOT `use-overview.ts` / `use-time-saved-series.ts`, which never
 * had it and so already blanked correctly — hand the page back the PREVIOUS
 * filter set's payload while the new request is in flight. Those are correct, concrete numbers for
 * a question the reader has stopped asking, and he reads them as the answer to
 * what he just clicked (owner's ruling, 2026-08-20: while the recount runs,
 * take the values and the charts away and say a recount is running).
 *
 * `isPlaceholderData` is true exactly in that window — the payload came from a
 * DIFFERENT query key, and on this dashboard every key element is part of the
 * question. That is not an accident: the one thing that was pure arrangement —
 * the per-table sort of an already-loaded table — is kept out of keys and out
 * of request URLs entirely by `filtersQuestion` (lib/filters-schema.ts), and
 * every sort a SERVER applies here is followed by a LIMIT, so it returns a
 * different SET of rows rather than the same ones reordered (owner's ruling 3,
 * 2026-08-20). If a query ever does sort a complete result server-side, this
 * is where it would need a presentation half; until then there is nothing to
 * carve out. It is the signal, and `isFetching` is not: `isFetching`
 * is also true of a routine background refresh of the SAME key, where the
 * numbers on screen ARE this filter set's, and gating on it would blank the
 * whole dashboard on every refetch.
 *
 * In that window this wrapper reports the query as what it actually is: not
 * loaded yet. `data` goes away and the pending flags go up, which is bit for
 * bit the state of a first load — the state every widget on this dashboard
 * already renders as a reserved-height skeleton. So no caller needs its own
 * branch, no tab invents its own spinner, and nothing renders an absence as a
 * quantity (a `0`, a `—`, a greyed old value).
 *
 * `isPlaceholderData` itself is left alone, so a caller that needs to tell
 * "recount" apart from "first load" still can.
 *
 * Cost of the honesty: switching to a filter set that is already cached still
 * resolves instantly (React Query serves it from cache, never as placeholder),
 * so this costs a skeleton only when there is genuinely nothing to show.
 */

import {
  useQuery,
  type QueryKey,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';

export function useFilteredQuery<TData, TError = Error>(
  options: UseQueryOptions<TData, TError, TData, QueryKey>,
): UseQueryResult<TData, TError> {
  const query = useQuery(options);
  // Three terms, and each one is a state this got wrong once.
  //
  // `isPlaceholderData` — the payload came from a DIFFERENT query key. That is
  // the bug: a new filter, an old answer.
  //
  // `isFetching` — a DISABLED query (`enabled: false`; Feedback does it on an
  // inverted rating range) keeps its placeholder payload for as long as it
  // stays disabled, with nothing in flight. Synthesising "still counting"
  // there strands the tab on a skeleton that never resolves, waiting for a
  // request nobody made.
  //
  // `isPaused` — and this is the one that looks redundant and is not. React
  // Query's default `networkMode: 'online'` gives a fetch started while
  // OFFLINE `fetchStatus: 'paused'`, not `'fetching'`. That request HAS been
  // made and resumes on reconnect, so dropping out here would hand the page
  // the previous filter set's numbers under the new filter bar — with the
  // notice invisible, because nothing reports as loading — for as long as the
  // connection is down. A recount is a request that is on its way, whether or
  // not the network is carrying it this second.
  if (!query.isPlaceholderData || (!query.isFetching && !query.isPaused)) {
    return query;
  }
  // A double cast, and it is load-bearing rather than lazy typing: React Query
  // types a pending result as `isPlaceholderData: false`, because on its own
  // terms these two states cannot coexist. This state is precisely the one it
  // has no member for — pending, yet knowing that what it is holding belongs
  // to the previous key. Every other field is flipped consistently with
  // 'pending' (data absent, status pending, isLoading true), so no consumer
  // sees a half-state; `isPlaceholderData` is deliberately left true so a
  // caller can still tell a recount apart from a first load.
  return {
    ...query,
    data: undefined,
    status: 'pending',
    isPending: true,
    isSuccess: false,
    isLoading: true,
  } as unknown as UseQueryResult<TData, TError>;
}
